import { FunctionComponent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { faEye, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import {
	useAddSmartListSourceMutation,
	usePreviewSmartListCountMutation,
	useUpdateSmartListSourceMutation,
} from "@/pages/Private/redux/list/list.api";
import { SmartListSource } from "@/pages/Private/pages/List/schema/list";
import { stripRelativeDateFields } from "@/pages/Private/pages/List/utils/relativeDateUtils";
import { useSingleFlight } from "@/utils/useSingleFlight";

import {
	ReengageSourceFormSchema,
	ReengageSourceFormValues,
	emptyReengageForm,
} from "../schema/reengageSource";
import { buildReengageFilter, parseReengageFilter } from "../utils/buildReengageFilter";
import { sourceRequestErrorMessage } from "../utils/sourceRequestError";
import { LeadScoringFiltersCard } from "./LeadScoringFiltersCard";
import { ReengagementCriteriaCard } from "./ReengagementCriteriaCard";

interface AddSourceFormProps {
	listId: number;
	editingSource: SmartListSource | null;
	onSaved: () => void;
	onCancelEdit: () => void;
}

/** Serialises the curated form into a stored filter string (relative date fields stripped). */
const serializeFilter = (values: ReengageSourceFormValues): string => {
	const filter = buildReengageFilter(values);
	const stripped = Object.fromEntries(
		Object.entries(filter).map(([key, entry]) => [key, stripRelativeDateFields(entry)])
	);

	return JSON.stringify(stripped);
};

export const AddSourceForm: FunctionComponent<AddSourceFormProps> = ({
	listId,
	editingSource,
	onSaved,
	onCancelEdit,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.reengage.${key}`);

	const [addSource, { isLoading: isAdding }] = useAddSmartListSourceMutation();
	const [updateSource, { isLoading: isUpdating }] = useUpdateSmartListSourceMutation();
	const [countProspects, { isLoading: isCounting }] = usePreviewSmartListCountMutation();
	const [previewCount, setPreviewCount] = useState<number | null>(null);
	const [requestError, setRequestError] = useState<string | null>(null);
	// A double click lands before the button renders as disabled; the second submit is dropped.
	const submitFlight = useSingleFlight();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ReengageSourceFormValues>({
		resolver: zodResolver(ReengageSourceFormSchema),
		defaultValues: emptyReengageForm(),
	});

	useEffect(() => {
		if (editingSource) {
			reset(parseReengageFilter(editingSource.filter, editingSource.name));
		} else {
			reset(emptyReengageForm());
		}

		setPreviewCount(null);
		setRequestError(null);
	}, [editingSource, reset]);

	const onSubmit = async (values: ReengageSourceFormValues) => {
		const payload = { name: values.name.trim(), filter: serializeFilter(values) };

		setRequestError(null);

		try {
			if (editingSource) {
				await updateSource({ listId, sourceId: editingSource.id, ...payload }).unwrap();
			} else {
				await addSource({ listId, ...payload }).unwrap();
			}
		} catch (error) {
			setRequestError(sourceRequestErrorMessage(error, t("errors.saveFailed")));

			return;
		}

		reset(emptyReengageForm());
		onSaved();
	};

	const handlePreview = handleSubmit(async (values) => {
		const filter = JSON.stringify(buildReengageFilter(values));

		setRequestError(null);
		setPreviewCount(null);

		try {
			const result = await countProspects({ filter }).unwrap();

			setPreviewCount(result?.count ?? 0);
		} catch (error) {
			setRequestError(sourceRequestErrorMessage(error, t("errors.wentWrong")));
		}
	});

	return (
		<form
			className="bg-white border border-border rounded-xl p-6"
			onSubmit={(event) => {
				event.preventDefault();
				void submitFlight.run(() => handleSubmit(onSubmit)());
			}}
		>
			<div className="text-base font-semibold text-text mb-1">
				{editingSource ? ts("editSourceTitle") : ts("addSourceTitle")}
			</div>
			<div className="text-[13px] text-text-meta mb-5">{ts("addSourceSub")}</div>

			<div className="mb-4">
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<InputField
							error={errors.name ? ts("requiredField") : undefined}
							handleChange={field.onChange}
							label={ts("sourceName")}
							name="name"
							placeholder={ts("sourceNamePlaceholder")}
							value={field.value}
						/>
					)}
				/>
			</div>

			<ReengagementCriteriaCard
				control={control}
				lastContactError={!!errors.lastContactRange}
				listId={listId}
			/>

			<LeadScoringFiltersCard control={control} listId={listId} />

			{requestError && (
				<p
					className="mb-4 text-[13px] text-status-error"
					data-testid="source-request-error"
					role="alert"
				>
					{requestError}
				</p>
			)}

			<div className="flex items-center justify-between gap-3 pt-5 border-t border-border">
				<div className="flex items-center gap-3">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-3.5 h-3.5 mr-1" icon={faEye} />}
						isLoading={isCounting}
						title={ts("previewMatching")}
						type="button"
						onClick={handlePreview}
					/>
					{previewCount !== null && (
						<span className="text-[13px] text-text-meta">
							{ts("previewResult")}: <strong className="text-text">{previewCount}</strong>
						</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					{editingSource && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title={t("basics.cancel")}
							type="button"
							onClick={onCancelEdit}
						/>
					)}
					<Button
						color={ButtonColor.BRAND}
						image={<Icon className="w-3.5 h-3.5 mr-1" icon={faPlus} />}
						isLoading={isAdding || isUpdating || submitFlight.isRunning}
						title={editingSource ? ts("saveSource") : ts("addSource")}
						type="submit"
					/>
				</div>
			</div>
		</form>
	);
};
